import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { motion } from "framer-motion";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { NotebookPen, Save, TrendingUp, TrendingDown } from "lucide-react";
import { API_URL } from "@/lib/api";
import { formatDateToYMD, normalizeExecutionDate, parseYMDToLocalDate } from "@/lib/date";
import { toast } from "sonner";

const API = API_URL;

export default function CalendarView({ currentStrategy, mode }) {
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const notesByDate = Object.fromEntries(notes.map((note) => [note.date, note.note]));
  const selectedDateKey = formatDateToYMD(selectedDate);
  const selectedMonth = selectedDateKey.slice(0, 7);

  const loadCalendarData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/calendar/${currentStrategy.id}?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error("Error loading calendar data:", error);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, [currentStrategy.id, mode]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const loadNotes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/day-notes?mode=${mode}&month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(response.data);
    } catch (error) {
      console.error("Error loading day notes:", error);
    }
  }, [mode, selectedMonth]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    setNoteDraft(notesByDate[selectedDateKey] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey, notes]);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/day-notes/${selectedDateKey}?mode=${mode}`,
        { note: noteDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(noteDraft.trim() ? "Day note saved" : "Day note removed");
      await loadNotes();
    } catch (error) {
      toast.error("Failed to save the day note");
    } finally {
      setSavingNote(false);
    }
  };

  const getDateData = (date) => {
    const dateStr = formatDateToYMD(date);
    return calendarData.find((d) => normalizeExecutionDate(d.date) === dateStr);
  };

  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const dateData = getDateData(date);
      const hasNote = Boolean(notesByDate[formatDateToYMD(date)]);
      const noteDot = hasNote ? (
        <span className="mx-auto mt-0.5 block h-1.5 w-1.5 rounded-full bg-primary" />
      ) : null;
      if (dateData) {
        return (
          <div className="text-xs mt-1">
            <span
              className={`font-semibold ${
                dateData.profit_loss >= 0 ? "text-success" : "text-destructive"
              }`}
            >
              {dateData.profit_loss >= 0 ? "+" : ""}
              {dateData.profit_loss}
            </span>
            {noteDot}
          </div>
        );
      }
      if (hasNote) {
        return <div className="mt-1">{noteDot}</div>;
      }
    }
    return null;
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const dateData = getDateData(date);
      if (dateData) {
        return dateData.profit_loss >= 0 ? "bg-success/10" : "bg-destructive/10";
      }
    }
    return null;
  };

  const selectedDateData = getDateData(selectedDate);

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <Skeleton className="h-10 w-40 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <Card className="lg:col-span-2 shadow-lg">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-56" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full rounded" />
            </CardContent>
          </Card>
          <Card className="shadow-lg">
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
        <Card className="mt-4 md:mt-6 shadow-lg">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="calendar-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Calendar</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Trading history for strategy: {currentStrategy.name}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <Card className="lg:col-span-2 shadow-lg" data-testid="calendar-card">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Calendar View</CardTitle>
              <CardDescription>
                Click on a date to see trade details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Calendar
                onChange={setSelectedDate}
                value={selectedDate}
                tileContent={tileContent}
                tileClassName={tileClassName}
                locale="en-US"
                className="w-full"
              />
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="date-details-card">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Day Details</CardTitle>
              <CardDescription className="text-sm">
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedDateData ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Number of Trades</p>
                      <p className="text-2xl font-bold" data-testid="date-trade-count">{selectedDateData.trade_count}</p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      selectedDateData.profit_loss >= 0
                        ? "bg-success/10"
                        : "bg-destructive/10"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Daily Profit/Loss
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        {selectedDateData.profit_loss >= 0 ? (
                          <TrendingUp className="h-5 w-5 text-success" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-destructive" />
                        )}
                        <p
                          className={`text-xl md:text-2xl font-bold ${
                            selectedDateData.profit_loss >= 0
                              ? "text-success"
                              : "text-destructive"
                          }`}
                          data-testid="date-profit-loss"
                        >
                          {selectedDateData.profit_loss >= 0 ? "+" : ""}
                          {selectedDateData.profit_loss} {currentStrategy.currency}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-trades-for-date">
                  <p className="text-muted-foreground">No trades on this date</p>
                </div>
              )}

              <div className="mt-4 space-y-2 border-t border-border pt-4">
                <Label htmlFor="day-note" className="flex items-center gap-2 text-sm">
                  <NotebookPen className="h-4 w-4 text-primary" />
                  Day note
                </Label>
                <Textarea
                  id="day-note"
                  data-testid="day-note-input"
                  placeholder="How did the session go? Context, mood, mistakes…"
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  rows={4}
                  className="resize-none text-sm"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={saveNote}
                  disabled={savingNote}
                  data-testid="day-note-save"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {savingNote ? "Saving…" : "Save note"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4 md:mt-6 shadow-lg" data-testid="monthly-summary-card">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl">Monthly Summary</CardTitle>
            <CardDescription>
              Statistics for the selected month
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const currentMonth = selectedDate.getMonth();
              const currentYear = selectedDate.getFullYear();
              const monthlyData = calendarData.filter((d) => {
                const date = parseYMDToLocalDate(normalizeExecutionDate(d.date));
                return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
              });

              const totalProfitLoss = monthlyData.reduce((sum, d) => sum + d.profit_loss, 0);
              const totalTrades = monthlyData.reduce((sum, d) => sum + d.trade_count, 0);
              const tradingDays = monthlyData.length;

              return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-secondary/50">
                    <p className="text-sm font-medium text-muted-foreground">Trading Days</p>
                    <p className="text-2xl font-bold" data-testid="monthly-trading-days">{tradingDays}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-secondary/50">
                    <p className="text-sm font-medium text-muted-foreground">Total Trades</p>
                    <p className="text-2xl font-bold" data-testid="monthly-total-trades">{totalTrades}</p>
                  </div>
                  <div
                    className={`p-4 rounded-lg ${
                      totalProfitLoss >= 0 ? "bg-success/10" : "bg-destructive/10"
                    }`}
                  >
                    <p className="text-sm font-medium text-muted-foreground">
                      Monthly P/L
                    </p>
                    <p
                      className={`text-xl md:text-2xl font-bold ${
                        totalProfitLoss >= 0 ? "text-success" : "text-destructive"
                      }`}
                      data-testid="monthly-profit-loss"
                    >
                      {totalProfitLoss >= 0 ? "+" : ""}
                      {totalProfitLoss.toFixed(2)} {currentStrategy.currency}
                    </p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
